import { ScoutStatusLabel } from "@clientFrontend/components/ScoutStatusLabel";
import { Fragment } from "react";
import * as styles from "./styles.css";
import { type ActionedWorks, ActionedWorksInfo } from "./uiParts/ActionedWorksInfo";
import { WorksInfo } from "./uiParts/WorksInfo";
import { ListCell } from "./uiParts/ListCell";
import { LIST_MAX_LENGTH } from "./constants";
import { UserDetail } from "./uiParts/UserDetail";

type UserProfile = {
  age: string;
  sex: string;
  address: string;
  stations: string[];
  hoppedCities: string[];
  hasNationalQualifications: boolean;
  hopedJobTypes: string[];
  credentials: string[];
  selfPR?: string;
};

export type User = {
  id: number;
  isHot: boolean;
  isNew: boolean;
  enteredWork?: "this" | "another";
  isBlocked?: boolean;
  profile: UserProfile;
  actionedWorksList: ActionedWorks[];
  scoutedWorks: string[];
  enteredWorks: string[];
  userDetailFetchUrl?: string;
};

export type UserTableProps = {
  selectedUserIds: number[];
  onSelectedUserChange: (userIds: number[]) => void;
  users: User[];
  isBundled?: boolean;
};

export const UserTable = (props: UserTableProps) => {
  const { selectedUserIds, onSelectedUserChange, users, isBundled } = props;

  const handleSelectCheckboxChange = (checkedUserId: number, isChecked: boolean) => {
    onSelectedUserChange(
      isChecked
        ? [...selectedUserIds, checkedUserId]
        : selectedUserIds.filter((selectedUserId) => selectedUserId !== checkedUserId),
    );
  };

  return (
    <>
      {isBundled && (
        <p className={`${styles.userCountText} js-new-scout-user-total-count-outer`}>
          <span id="js-new-scout-user-total-count" className={styles.userCountNumber}>
            {selectedUserIds.length}
          </span>
          人が一括選択されています
        </p>
      )}
      <table className={styles.table}>
        <thead>
          <tr>
            {isBundled && (
              <th className={`${styles.th} ${styles.w5} ${styles.fs10}`}>
                一括
                <br />
                スカウト
              </th>
            )}
            <th className={`${styles.th} ${styles.w10}`}>ステータス</th>
            <th className={`${styles.th} ${styles.w20}`}>基本情報</th>
            <th className={`${styles.th} ${styles.w20}`}>希望職種 / 経験</th>
            <th className={`${styles.th} ${styles.w20}`}>資格</th>
            <th className={`${styles.th} ${styles.w20}`}>自己PR</th>
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {users.map((user, index) => (
            <Fragment key={user.id}>
              <tr className={index % 2 === 0 ? "" : styles.bg}>
                {/* 一括スカウト */}
                {isBundled && (
                  <td
                    className={`${styles.td} ${styles.userDetailTd} ${styles.bundleTd} ${styles.fs10}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.id)}
                      onChange={(e) => handleSelectCheckboxChange(user.id, e.currentTarget.checked)}
                      aria-label={`${index + 1}番目のユーザーを選択`}
                    />
                  </td>
                )}
                {/* ステータス */}
                <td className={`${styles.td} ${styles.userDetailTd} ${styles.statusTd}`}>
                  <div className={styles.statusContainer}>
                    {user.isHot && <ScoutStatusLabel status="hot">HOT</ScoutStatusLabel>}
                    {user.isNew && <ScoutStatusLabel status="new">NEW</ScoutStatusLabel>}
                    {user.enteredWork === "this" && (
                      <ScoutStatusLabel status="applied">応募済み</ScoutStatusLabel>
                    )}
                    {user.enteredWork === "another" && (
                      <ScoutStatusLabel status="applied">他の求人へ応募済み</ScoutStatusLabel>
                    )}
                  </div>
                </td>
                {/* 基本情報 */}
                <td className={`${styles.td} ${styles.userDetailTd}`}>
                  {user.isBlocked && (
                    <p className={styles.alert}>この求職者にはスカウトを送ることができません</p>
                  )}
                  <p>
                    <strong>{`［${user.profile.age}歳 / ${user.profile.sex}］`}</strong>
                  </p>
                  <p> 住所：{user.profile.address}</p>
                  {user.profile.stations.length > 0 && (
                    <p>{user.profile.stations.map((station) => `${station}駅`).join(" | ")}</p>
                  )}
                  {user.profile.hoppedCities.length > 0 && (
                    <p>
                      {user.profile.hoppedCities.slice(0, LIST_MAX_LENGTH).join(" / ")}
                      {user.profile.hoppedCities.length > LIST_MAX_LENGTH && <> など</>}
                    </p>
                  )}
                  区分：{user.profile.hasNationalQualifications ? "国家" : "民間"}
                </td>
                {/* 希望職種／経験 */}
                <ListCell
                  items={user.profile.hopedJobTypes}
                  ariaLabel={`${index + 1}番目のユーザーの希望職種`}
                />
                {/* 資格 */}
                <ListCell
                  items={user.profile.credentials}
                  ariaLabel={`${index + 1}番目のユーザーの資格`}
                />
                {/* 自己PR */}
                <td className={`${styles.td} ${styles.userDetailTd} ${styles.selfPRTd}`}>
                  {user.profile.selfPR}
                </td>
              </tr>
              {user.actionedWorksList.map((actionedWorks) => (
                <ActionedWorksInfo
                  key={actionedWorks.type}
                  actionedWorks={actionedWorks}
                  containerClassName={index % 2 === 0 ? "" : styles.bg}
                />
              ))}
              {/* スカウト済み求人情報 */}
              {user.scoutedWorks.length > 0 && (
                <WorksInfo
                  header="こちらの求人から30日以内にスカウト済みです："
                  workNames={user.scoutedWorks}
                  tableRowClassName={index % 2 === 0 ? "" : styles.bg}
                />
              )}
              {/* 応募済み求人情報 */}
              {user.enteredWorks.length > 0 && (
                <WorksInfo
                  header="こちらの求人は過去応募済みです："
                  workNames={user.enteredWorks}
                  tableRowClassName={index % 2 === 0 ? "" : styles.bg}
                />
              )}
              {/* プロフィールの詳細 */}
              {user.userDetailFetchUrl && (
                <UserDetail
                  userDetailFetchUrl={user.userDetailFetchUrl}
                  tableRowClassName={index % 2 === 0 ? "" : styles.bg}
                />
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};
